import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PublicIngestTab } from "@/components/PublicIngestTab";
import { ProductImagesTab } from "@/components/ProductImagesTab";
import { OutreachTab } from "@/components/OutreachTab";
import { OperatorAnalyticsTab } from "@/components/OperatorAnalyticsTab";
import { AnnouncementsTab } from "@/components/AnnouncementsTab";
import { UsersTab } from "@/components/UsersTab";
import { ModerationTab } from "@/components/ModerationTab";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Shield,
  Plus,
  Upload,
  Copy,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Trash2,
  Loader2,
  Building2,
  Link2,
  Sprout,
  Image as ImageIcon,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/time";

export default function AdminConsole() {
  const { user: dispensaryUser, authChecked } = useApp();
  const { user: consumerUser, loading: consumerLoading } = useConsumerAuth();
  const principal = dispensaryUser || consumerUser;
  const navigate = useNavigate();
  const [whoami, setWhoami] = useState(null);
  const [allowed, setAllowed] = useState(null);
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    // Only redirect if BOTH auth systems have finished checking AND neither
    // has a logged-in user. Otherwise we'd kick a Google-signed-in admin to
    // the dispensary login page on first paint.
    if (authChecked && !consumerLoading && !principal) {
      navigate("/account?next=/admin");
    }
  }, [authChecked, consumerLoading, principal, navigate]);

  const refresh = () => {
    Promise.all([
      api.get("/admin/whoami").catch((e) => ({ data: null, _err: e })),
      api.get("/admin/dispensaries").catch((e) => ({ data: null, _err: e })),
      api.get("/admin/trulieve-only").catch(() => ({ data: null })),
    ]).then(([w, d, t]) => {
      setWhoami(w.data);
      setAllowed(w.data?.is_admin === true);
      if (w.data?.is_admin && d.data) {
        setData({ ...d.data, trulieve_only: !!t.data?.enabled });
      }
    });
  };

  useEffect(() => {
    if (principal) refresh();
  }, [principal]);

  if (!principal) return null;
  if (allowed === null)
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );

  if (!allowed) return <NoAccess email={principal.email} />;

  if (!data)
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );

  return (
    <main className="container-narrow py-10">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-accent" />
            Admin · Concierge
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl">Bud Link operator console</h1>
          <p className="text-sm text-foreground/60 mt-1">
            Onboard shops on their behalf, issue claim tokens, toggle demo
            data.
          </p>
        </div>
        <div
          data-testid="admin-actions-row"
          className="flex flex-wrap gap-2 w-full sm:w-auto"
        >
          <SeedAnchorsButton onSeeded={refresh} />
          <ApplyBrandPhotosButton onApplied={refresh} />
          <RefreshStrainPhotosButton onRefreshed={refresh} />
          <MigrateCrescoButton onMigrated={refresh} />
          <ExportSubscribersButton />
          <Button
            data-testid="admin-add-dispensary"
            onClick={() => setShowAdd(true)}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-auto min-h-10 px-4 sm:px-6 py-2 whitespace-normal text-left leading-tight"
          >
            <Plus className="w-4 h-4 mr-2 shrink-0" />
            Add a dispensary
          </Button>
        </div>
      </header>

      <DemoModeCard initial={data.demo_mode} onChange={refresh} />
      <TrulieveOnlyCard initial={data.trulieve_only} onChange={refresh} />

      <Stats data={data} />

      <Tabs defaultValue="dispensaries">
        <TabsList
          className="bg-card border border-border p-1 mb-6 flex flex-wrap gap-1 h-auto w-full sm:w-auto"
          data-testid="admin-tablist"
        >
          <TabsTrigger value="dispensaries" data-testid="admin-tab-dispensaries" className="whitespace-normal text-left leading-tight">
            Dispensaries
          </TabsTrigger>
          <TabsTrigger value="announcements" data-testid="admin-tab-announcements" className="whitespace-normal text-left leading-tight">
            Announcements
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="whitespace-normal text-left leading-tight">
            Users
          </TabsTrigger>
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation" className="whitespace-normal text-left leading-tight">
            Moderation
          </TabsTrigger>
          <TabsTrigger value="public-ingest" data-testid="admin-tab-public-ingest" className="whitespace-normal text-left leading-tight">
            Public ingest
          </TabsTrigger>
          <TabsTrigger value="product-images" data-testid="admin-tab-product-images" className="whitespace-normal text-left leading-tight">
            Product photos
          </TabsTrigger>
          <TabsTrigger value="outreach" data-testid="admin-tab-outreach" className="whitespace-normal text-left leading-tight">
            Outreach
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="whitespace-normal text-left leading-tight">
            Analytics
          </TabsTrigger>
        </TabsList>
        <TabsContent value="dispensaries">
          <DispensaryTable data={data} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="public-ingest">
          <PublicIngestTab />
        </TabsContent>
        <TabsContent value="announcements">
          <AnnouncementsTab />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
        <TabsContent value="moderation">
          <ModerationTab />
        </TabsContent>
        <TabsContent value="product-images">
          <ProductImagesTab />
        </TabsContent>
        <TabsContent value="outreach">
          <OutreachTab />
        </TabsContent>
        <TabsContent value="analytics">
          <OperatorAnalyticsTab />
        </TabsContent>
      </Tabs>

      <AddConciergeDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={(d) => {
          setShowAdd(false);
          refresh();
          // immediately show claim link
          showClaimToast(d);
        }}
      />
    </main>
  );
}

const showClaimToast = (d) => {
  const link = `${window.location.origin}/claim?slug=${d.slug}&token=${d.claim_token}`;
  navigator.clipboard?.writeText(link).catch(() => null);
  toast.success("Claim link copied to clipboard", {
    description: `Send to ${d.owner_email || d.name} so they can take ownership.`,
    duration: 8000,
  });
};

const SeedAnchorsButton = ({ onSeeded }) => {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (
      !window.confirm(
        "Seed PA anchors + sync live Sunnyside stores + remove junk test rows? Safe to re-run (idempotent)."
      )
    )
      return;
    setBusy(true);
    try {
      const r = await api.post("/admin/seed-pa-anchors");
      const {
        inserted = 0,
        skipped = 0,
        removed_junk = 0,
        sunnyside_sync = {},
        trulieve_sync = {},
        image_borrow = {},
        total_real_pa_shops = 0,
      } = r.data || {};
      const sIns = sunnyside_sync.inserted || 0;
      const sUpd = sunnyside_sync.updated || 0;
      const sRem = sunnyside_sync.removed || 0;
      const sTot = sunnyside_sync.total_pa || 0;
      const tIns = trulieve_sync.inserted || 0;
      const tUpd = trulieve_sync.updated || 0;
      const tTot = trulieve_sync.total_pa || 0;
      const headline =
        inserted || sIns || tIns
          ? `✅ Seed complete — ${inserted} anchors + ${sIns} Sunnyside + ${tIns} Trulieve added`
          : `✅ Re-sync complete — DB is up to date`;
      const lines = [
        `${total_real_pa_shops} real PA shops in DB now`,
        sTot
          ? `Sunnyside: ${sTot} PA stores synced (${sUpd} refreshed, ${sIns} new${sRem ? `, ${sRem} stale removed` : ""})`
          : `Sunnyside sync: ${sunnyside_sync.error || "did not run"}`,
        tTot
          ? `Trulieve: ${tTot} PA stores synced (${tUpd} refreshed, ${tIns} new)`
          : `Trulieve sync: ${trulieve_sync.error || "did not run"}`,
        removed_junk ? `🧹 Removed ${removed_junk} junk test rows` : null,
        image_borrow.products_fixed || image_borrow.strains_fixed
          ? `🖼️ Borrowed ${image_borrow.products_fixed} product + ${image_borrow.strains_fixed} strain images cross-site`
          : null,
        `Next: hit "Run all (force)" in Public Ingest to pull live menus`,
      ].filter(Boolean);
      toast.success(headline, {
        description: lines.join(" · "),
        duration: 16000,
      });
      onSeeded && onSeeded();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid="admin-seed-anchors"
      onClick={click}
      disabled={busy}
      variant="outline"
      className="rounded-full h-10 px-5 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Sprout className="w-4 h-4 mr-2" />
      )}
      Seed PA anchors
    </Button>
  );
};

const ApplyBrandPhotosButton = ({ onApplied }) => {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/apply-brand-photos");
      const { applied = 0, by_rule = {} } = r.data || {};
      const top = Object.entries(by_rule)
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([k, n]) => `${k.replace("cresco_", "")} ${n}`)
        .join(" · ");
      toast.success(`✅ ${applied} Cresco products refreshed`, {
        description: top || "All curated rules applied.",
        duration: 12000,
      });
      onApplied && onApplied();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid="admin-apply-brand-photos"
      onClick={click}
      disabled={busy}
      variant="outline"
      className="rounded-full h-auto min-h-10 px-4 sm:px-5 py-2 border-amber-500/40 text-amber-200 hover:bg-amber-500/10 whitespace-normal text-left leading-tight"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
      ) : (
        <ImageIcon className="w-4 h-4 mr-2 shrink-0" />
      )}
      Apply Cresco photos
    </Button>
  );
};


const RefreshStrainPhotosButton = ({ onRefreshed }) => {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/refresh-strain-photos");
      const { updated = 0, unchanged = 0, no_listings = 0 } = r.data || {};
      toast.success(
        updated
          ? `Refreshed ${updated} strain photo${updated === 1 ? "" : "s"} from live product data`
          : "Strain catalog photos already up to date.",
        {
          description: `${updated} updated · ${unchanged} unchanged · ${no_listings} have no live listings yet`,
          duration: 10000,
        }
      );
      onRefreshed && onRefreshed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid="admin-refresh-strain-photos"
      onClick={click}
      disabled={busy}
      variant="outline"
      className="rounded-full h-auto min-h-10 px-4 sm:px-5 py-2 border-amber-500/40 text-amber-200 hover:bg-amber-500/10 whitespace-normal text-left leading-tight"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
      ) : (
        <ImageIcon className="w-4 h-4 mr-2 shrink-0" />
      )}
      Heal strain catalog
    </Button>
  );
};


const MigrateCrescoButton = ({ onMigrated }) => {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/migrate-cresco-to-sunnyside");
      const { renamed = [], removed_dupes = [], coming_soon_slugs = [] } = r.data || {};
      const summary = renamed
        .map((x) => `${x.from} → ${x.to}${x.coming_soon ? " (coming soon)" : ""}`)
        .join(" · ");
      toast.success(
        renamed.length
          ? `✅ Rebadged ${renamed.length} Cresco/Yeltrah row${renamed.length === 1 ? "" : "s"} → Sunnyside`
          : "No Cresco/Yeltrah rows found — already migrated.",
        {
          description:
            summary
              ? `${summary}${removed_dupes.length ? ` · removed ${removed_dupes.length} dup${removed_dupes.length === 1 ? "" : "s"}` : ""}${coming_soon_slugs.length ? ` · ${coming_soon_slugs.length} marked coming soon` : ""}`
              : undefined,
          duration: 14000,
        }
      );
      onMigrated && onMigrated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid="admin-migrate-cresco"
      onClick={click}
      disabled={busy}
      variant="outline"
      className="rounded-full h-auto min-h-10 px-4 sm:px-5 py-2 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 whitespace-normal text-left leading-tight"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
      ) : (
        <Shield className="w-4 h-4 mr-2 shrink-0" />
      )}
      Rebadge Cresco → Sunnyside
    </Button>
  );
};

const ExportSubscribersButton = () => {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/subscriptions/export?active_only=true");
      const rows = r.data?.results || [];
      if (rows.length === 0) {
        toast.success("No subscribers yet", {
          description: "Once people opt in on the home page or footer, you'll be able to export them here.",
        });
        return;
      }
      // Build CSV: email, zip, source, created_at
      const header = "email,zip,source,created_at";
      const csv = [header]
        .concat(
          rows.map((r2) =>
            [
              r2.email || "",
              r2.zip || "",
              (r2.source || "").replace(/[",\n]/g, " "),
              r2.created_at || "",
            ]
              .map((v) => `"${String(v).replace(/"/g, '""')}"`)
              .join(",")
          )
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `budlink-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rows.length} subscriber${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid="admin-export-subscribers"
      onClick={click}
      disabled={busy}
      variant="outline"
      className="rounded-full h-auto min-h-10 px-4 sm:px-5 py-2 border-sky-500/40 text-sky-200 hover:bg-sky-500/10 whitespace-normal text-left leading-tight"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
      ) : (
        <Download className="w-4 h-4 mr-2 shrink-0" />
      )}
      Export subscribers
    </Button>
  );
};

const NoAccess = ({ email }) => (
  <div className="container-narrow py-20">
    <div className="max-w-md mx-auto border border-border bg-card rounded-2xl p-8 text-center">
      <Shield className="w-8 h-8 mx-auto text-muted-foreground mb-4" />
      <h1 className="font-serif text-2xl mb-2">Admin access required</h1>
      <p className="text-sm text-foreground/60 mb-4">
        The account <code className="text-foreground/80">{email}</code> is not
        on the admin allow-list yet.
        <br />
        <br />
        Ask Emergent Support to add it to the
        {" "}<code className="text-foreground/80">ADMIN_EMAILS</code> env var on this deployment.
      </p>
      <Link to="/dispensary/dashboard" className="pill pill-primary">
        Back to dashboard
      </Link>
    </div>
  </div>
);

const DemoModeCard = ({ initial, onChange }) => {
  const [enabled, setEnabled] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setEnabled(initial), [initial]);

  const toggle = async (v) => {
    setBusy(true);
    setEnabled(v);
    try {
      await api.put("/admin/demo-mode", { enabled: v });
      toast.success(v ? "Demo data is visible to consumers" : "Demo data hidden");
      onChange && onChange();
    } catch (e) {
      setEnabled(!v);
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-border bg-card rounded-2xl p-5 mb-6 flex items-center justify-between gap-4"
      data-testid="admin-demo-mode-card"
    >
      <div className="flex items-center gap-4">
        <div
          className={`w-10 h-10 rounded-full inline-flex items-center justify-center ${
            enabled
              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
              : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
          }`}
        >
          {enabled ? (
            <AlertCircle className="w-4 h-4" />
          ) : (
            <CheckCircle2 className="w-4 h-4" />
          )}
        </div>
        <div>
          <div className="text-sm font-medium">
            Demo data {enabled ? "is showing" : "is hidden"}
          </div>
          <div className="text-xs text-muted-foreground">
            {enabled
              ? "Mock dispensaries appear to consumers — turn this off before going public."
              : "Public search shows only real dispensaries."}
          </div>
        </div>
      </div>
      <Switch
        data-testid="admin-demo-mode-toggle"
        checked={enabled}
        onCheckedChange={toggle}
        disabled={busy}
      />
    </div>
  );
};

const TrulieveOnlyCard = ({ initial, onChange }) => {
  const [enabled, setEnabled] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setEnabled(initial), [initial]);

  const toggle = async (v) => {
    setBusy(true);
    setEnabled(v);
    try {
      await api.put("/admin/trulieve-only", { enabled: v });
      toast.success(
        v
          ? "Public search now scoped to Trulieve only"
          : "Public search shows every dispensary again"
      );
      onChange && onChange();
    } catch (e) {
      setEnabled(!v);
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-border bg-card rounded-2xl p-5 mb-6 flex items-center justify-between gap-4"
      data-testid="admin-trulieve-only-card"
    >
      <div className="flex items-center gap-4">
        <div
          className={`w-10 h-10 rounded-full inline-flex items-center justify-center ${
            enabled
              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
              : "bg-foreground/10 text-foreground/60 border border-border"
          }`}
        >
          {enabled ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
        </div>
        <div>
          <div className="text-sm font-medium">
            Trulieve-only mode {enabled ? "is on" : "is off"}
          </div>
          <div className="text-xs text-muted-foreground">
            {enabled
              ? "Consumer search + dispensary lists show ONLY Trulieve shops. Other data is preserved in the DB."
              : "Every legal dispensary is visible to consumers."}
          </div>
        </div>
      </div>
      <Switch
        data-testid="admin-trulieve-only-toggle"
        checked={enabled}
        onCheckedChange={toggle}
        disabled={busy}
      />
    </div>
  );
};

const Stats = ({ data }) => {
  const real = data.dispensaries.filter((d) => !d.mock).length;
  const mock = data.dispensaries.filter((d) => d.mock).length;
  const owned = data.dispensaries.filter((d) => !!d.owner_id).length;
  const unclaimed = data.dispensaries.filter(
    (d) => !d.owner_id && d.claim_token
  ).length;
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6"
      data-testid="admin-stats"
    >
      <Stat label="Real shops" value={real} />
      <Stat label="Owned" value={owned} accent="emerald" />
      <Stat label="Pending claim" value={unclaimed} accent="amber" />
      <Stat label="Demo seed" value={mock} accent="muted" />
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="border border-border bg-card rounded-2xl p-4">
    <div className="eyebrow mb-1">{label}</div>
    <div
      className={`font-serif text-3xl ${
        accent === "emerald"
          ? "text-emerald-300"
          : accent === "amber"
            ? "text-amber-300"
            : accent === "muted"
              ? "text-muted-foreground"
              : ""
      }`}
    >
      {value}
    </div>
  </div>
);

const DispensaryTable = ({ data, onRefresh }) => {
  const [filter, setFilter] = useState("all");
  const filtered = data.dispensaries.filter((d) => {
    if (filter === "owned") return !!d.owner_id;
    if (filter === "pending") return !d.owner_id && d.claim_token;
    if (filter === "mock") return d.mock;
    return true;
  });

  return (
    <div className="border border-border bg-card rounded-2xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-border">
        <h3 className="font-serif text-xl">Dispensaries</h3>
        <div className="flex flex-wrap gap-1 text-xs">
          {[
            { id: "all", label: `All · ${data.dispensaries.length}` },
            { id: "owned", label: "Owned" },
            { id: "pending", label: "Pending claim" },
            { id: "mock", label: "Demo seed" },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`admin-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full transition whitespace-nowrap ${
                filter === f.id
                  ? "bg-foreground text-background"
                  : "text-foreground/70 hover:bg-secondary/60"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground text-sm">
          Nothing here yet.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {filtered.map((d) => (
            <Row key={d.slug} d={d} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
};

const Row = ({ d, onRefresh }) => {
  const status = d.owner_id
    ? { label: "Owned", color: "emerald" }
    : d.claim_token
      ? { label: "Pending claim", color: "amber" }
      : d.mock
        ? { label: "Demo seed", color: "muted" }
        : { label: "Listing only", color: "muted" };

  return (
    <div
      data-testid={`admin-row-${d.slug}`}
      className="px-5 py-4 flex flex-wrap items-center gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5 text-foreground/60" />
          <div className="font-medium truncate">{d.name}</div>
          <span
            className={`pill text-[10px] ${
              status.color === "emerald"
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                : status.color === "amber"
                  ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                  : "bg-muted text-muted-foreground border-border"
            }`}
          >
            {status.label}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate">
          {d.address} · {d.city || "—"} · {d.products_count} products
          {d.owner_email && (
            <span className="ml-2 text-foreground/60">· {d.owner_email}</span>
          )}
          {d.menu_updated_at && (
            <span className="ml-2">· menu {timeAgo(d.menu_updated_at)}</span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {!d.owner_id && d.claim_token && (
          <CopyClaimLink slug={d.slug} token={d.claim_token} />
        )}
        <UploadCsvButton slug={d.slug} onUploaded={onRefresh} />
        <Link
          to={`/dispensaries/${d.slug}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`admin-row-view-${d.slug}`}
          className="text-foreground/60 hover:text-foreground inline-flex items-center"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </Link>
        {!d.owner_id && (
          <button
            data-testid={`admin-row-delete-${d.slug}`}
            onClick={async () => {
              if (!window.confirm(`Delete "${d.name}"? This removes all products.`)) return;
              try {
                await api.delete(`/admin/dispensaries/${d.slug}`);
                toast.success("Deleted");
                onRefresh && onRefresh();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }}
            className="text-rose-300/70 hover:text-rose-300 inline-flex items-center"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const CopyClaimLink = ({ slug, token }) => {
  const [busy, setBusy] = useState(false);
  const copy = () => {
    const link = `${window.location.origin}/claim?slug=${slug}&token=${token}`;
    navigator.clipboard
      ?.writeText(link)
      .then(() => toast.success("Claim link copied"))
      .catch(() => toast.error("Could not copy"));
  };
  const rotate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/dispensaries/${slug}/rotate-claim-token`);
      const link = `${window.location.origin}/claim?slug=${slug}&token=${r.data.claim_token}`;
      navigator.clipboard?.writeText(link).catch(() => null);
      toast.success("New claim link copied", {
        description: "Old link is now invalid.",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1">
      <button
        data-testid={`admin-copy-claim-${slug}`}
        onClick={copy}
        className="pill text-[10px] bg-amber-500/15 text-amber-200 border-amber-500/40 hover:bg-amber-500/25"
      >
        <Link2 className="w-3 h-3" /> Copy claim link
      </button>
      <button
        data-testid={`admin-rotate-claim-${slug}`}
        onClick={rotate}
        disabled={busy}
        title="Rotate (invalidates old link)"
        className="text-foreground/50 hover:text-foreground"
      >
        {busy ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <RefreshCw className="w-3 h-3" />
        )}
      </button>
    </div>
  );
};

const UploadCsvButton = ({ slug, onUploaded }) => {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => inputRef.current?.click();
  const handle = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Choose a .csv file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("CSV > 5 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/dispensaries/${slug}/menu/csv-upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Imported ${r.data.products_upserted} products`, {
        description: r.data.errors?.length
          ? `${r.data.errors.length} rows skipped`
          : undefined,
      });
      onUploaded && onUploaded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handle}
        data-testid={`admin-csv-input-${slug}`}
      />
      <button
        data-testid={`admin-csv-pick-${slug}`}
        onClick={pick}
        disabled={busy}
        className="pill text-[10px] bg-emerald-500/15 text-emerald-200 border-emerald-500/40 hover:bg-emerald-500/25"
      >
        {busy ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Upload className="w-3 h-3" />
        )}
        CSV
      </button>
    </>
  );
};

const AddConciergeDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [pickup, setPickup] = useState(true);
  const [delivery, setDelivery] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !address) {
      toast.error("Name and address required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/dispensaries", {
        name,
        address,
        zip,
        phone,
        owner_email: ownerEmail || null,
        pickup,
        delivery,
      });
      onCreated(r.data.dispensary);
      setName(""); setAddress(""); setZip(""); setPhone(""); setOwnerEmail("");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-background border-border max-w-md"
        data-testid="admin-add-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Add a dispensary
          </DialogTitle>
          <DialogDescription className="text-foreground/60">
            You'll get a claim link to send the owner so they can take it over.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Shop name">
            <Input
              data-testid="admin-add-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Solevo Wellness — West Reading"
              autoFocus
            />
          </Field>
          <Field label="Address">
            <Input
              data-testid="admin-add-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="100 Penn Ave"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ZIP">
              <Input
                data-testid="admin-add-zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="19611"
                maxLength={5}
              />
            </Field>
            <Field label="Phone">
              <Input
                data-testid="admin-add-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="610-555-0100"
              />
            </Field>
          </div>
          <Field label="Owner email (optional)">
            <Input
              data-testid="admin-add-owner-email"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="manager@example.com"
            />
          </Field>
          <div className="flex items-center justify-between gap-3 border-y border-border py-3">
            <div className="flex items-center gap-3">
              <Switch checked={pickup} onCheckedChange={setPickup} />
              <span className="text-sm">Pickup</span>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={delivery} onCheckedChange={setDelivery} />
              <span className="text-sm">Delivery</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="admin-add-submit"
              disabled={busy}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" /> Create & copy claim link
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
